
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { createClient, getUserApiKey } from '@/lib/supabase-client'
import { type UserProfile, type UsageStats } from '../types'
import Sidebar from './Sidebar'
import DashboardHeader from './DashboardHeader'
import UsageStatsSection from './UsageStatsSection'

export default function AnalyticsPage() {
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [usage, setUsage] = useState<UsageStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedMonth, setSelectedMonth] = useState<string>('')
  const navigate = useNavigate()
  const supabase = createClient()

  useEffect(() => {
    loadData()
  }, [selectedMonth])

  const loadData = async () => {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) {
      navigate('/auth/login')
      return
    }

    const { data: profileData } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', session.user.id)
      .maybeSingle()

    const apiKey = await getUserApiKey(supabase, session.user.id)

    if (profileData) {
      setProfile({
        ...profileData,
        email: session.user.email || '',
        api_key: apiKey || '',
      } as UserProfile)
    } else {
      setProfile({
        id: session.user.id,
        name: session.user.user_metadata?.name || session.user.email?.split('@')[0] || 'User',
        email: session.user.email || '',
        api_key: apiKey || '',
        subscription_tier: 'free',
      } as UserProfile)
    }

    if (apiKey) {
        const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'https://eutekssvzhtkwzperkzb.supabase.co/functions/v1'
        // Use /usage for Vercel API, /usage-public for Supabase direct
        const usageEndpoint = apiBaseUrl.includes('supabase.co') ? '/usage-public' : '/usage'
        try {
          const response = await fetch(`${apiBaseUrl}${usageEndpoint}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ 
              api_key: apiKey,
              month: selectedMonth || undefined, // Send month if selected
            }),
          })
          if (response.ok) {
            const usageData = await response.json()
            setUsage(usageData)
          } else {
            const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
            setError(errorData.message || errorData.error || `Failed to load usage data (${response.status})`)
          }
        } catch (error: any) {
          setError(error.message || 'Failed to load usage data')
        }
      }

    setLoading(false)
  }

  // Generate list of available months (last 12 months)
  const getAvailableMonths = () => {
    const months: Array<{ value: string; label: string }> = []
    const now = new Date()
    const seenMonths = new Set<string>() // Track seen months to avoid duplicates
    
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1)
      const monthStr = date.toISOString().substring(0, 7) // "2025-01"
      
      // Only add if we haven't seen this month before
      if (!seenMonths.has(monthStr)) {
        seenMonths.add(monthStr)
        months.push({
          value: monthStr,
          label: date.toLocaleString('default', { month: 'long', year: 'numeric' })
        })
      }
    }
    
    return months
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-slate-900 flex items-center justify-center">
        <div className="text-center animate-fade-in">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 dark:border-primary-400"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800">
      <Sidebar />
      <div className="lg:pl-64">
        <DashboardHeader title="Usage & Analytics" />
        <main className="px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-8 animate-fade-in">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">Usage & Analytics</h1>
                <p className="text-lg text-gray-600 dark:text-gray-400">Track your API usage and view detailed statistics</p>
              </div>
              <div className="flex items-center space-x-3">
                <label htmlFor="month-select" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  View Month:
                </label>
                <select
                  id="month-select"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className="px-4 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">Current Month</option>
                  {getAvailableMonths().map((month, index) => (
                    <option key={`${month.value}-${index}`} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          {error && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-red-800 dark:text-red-200">{error}</p>
              <button
                onClick={() => {
                  setError(null)
                  loadData()
                }}
                className="mt-2 text-sm text-red-600 dark:text-red-400 hover:underline"
              >
                Try again
              </button>
            </div>
          )}
          {usage && <UsageStatsSection usage={usage} onRefresh={loadData} selectedMonth={selectedMonth} />}
          {!usage && !loading && !error && (
            <div className="text-center py-12">
              <p className="text-gray-500 dark:text-gray-400">No usage data available. Generate some alt text to see your statistics!</p>
            </div>
          )}
        </main>
      </div>
    </div>
  )
}

